import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showModal } from '@store/actions/commonAction';
import {
  searchSubscriptionSharedLpList,
  sendSharedLpSms,
  checkSharedLpSms,
  resetSharedLp,
  completeSharedLp,
} from '@store/actions/appListAction';
import SatButton from '@components/common/satUI/SatButton';
import JointSubscriptionRegistList from '@components/appList/JointSubscriptionRegistList';

const JointSubscriptionRegistPopup = props => {
  const { policyNo, applicationNo, planEstablishNo, setBizModal, onClose , lpDeptYn ,isLP } =
    props;
  const { subscriptionSharedLpList = [] } = useSelector(
    state => state.appListReducer,
  );

  const CERT_NUM_INPUT_MSG = '인증번호를 입력해주세요.';
  const CERT_NUM_INPUT_ERROR_MSG = '인증번호가 다릅니다. 다시 시도해주세요.';

  const lpRef1 = useRef();
  const lpRef2 = useRef();
  const lpRef3 = useRef();
  const { alert } = $hooks.useModal();

  // 입력 데이터
  const [inputStateLpList, setInputStateLpList] = useState([]);

  // 데이터 입력 불가 여부 (문자 발송 후 입력 불가, 인증 후 불가)
  const [isDisableInput, setIsDisableInput] = useState(false);

  // 최종 LP 순위
  const [lastLpOrder, setLastLpOrder] = useState(0);

  // 인증 번호
  const [certNumList, setCertNumList] = useState([]);

  const [certNumErrorList, setCertNumErrorList] = useState({
    status: '',
    msg: CERT_NUM_INPUT_MSG,
  });

  // 문자 전송 버튼 상태
  const [sendSmsButtonStatusList, setSendSmsButtonStatusList] = useState([]);

  const [lpRefList] = useState([lpRef1, lpRef2, lpRef3]);

  const dispatch = useDispatch();

  useEffect(() => {
    if (policyNo) handlerSearchList(policyNo);
  }, [policyNo]);

  useEffect(() => {
    setInputStateLpList(subscriptionSharedLpList);
    setLastLpOrder(subscriptionSharedLpList?.length);

    // 문자 전송 버튼 상태
    const isSmsButtonStatusInitiated =
      certNumList.length === sendSmsButtonStatusList.length - 1;
    if (!isSmsButtonStatusInitiated) {
      const smsButtonStatusList = subscriptionSharedLpList.map(data => ({
        isSendSms: false,
        disabled:
          // 이미 동의 완료 이거나, 비율이 입력이 안되었을 때 버튼 비활성화 처리
          // - 개별 버튼 비활성화 조건 외, 전체 공동 청약 설계사 정보에 대한 유효성은 따로 처리
          data.agreeYn === 'Y' || $utils.isEmptyString(data.ratio)
            ? true
            : false,
      }));
      setSendSmsButtonStatusList(smsButtonStatusList);
    }

    // 인증 번호
    const isCertiNumInitiated =
      certNumList.length === subscriptionSharedLpList.length - 1;
    if (!isCertiNumInitiated)
      setCertNumList(
        subscriptionSharedLpList
          .filter((_, index) => index !== 0)
          .map(() => Array(4).fill('')),
      );

    // 인증 번호 상태
    setCertNumErrorList(
      subscriptionSharedLpList
        .filter((_, index) => index !== 0)
        .map(() => ({ status: '', msg: CERT_NUM_INPUT_MSG })),
    );

    // setCertNumErrorList(
    //   subscriptionSharedLpList
    //     // .filter((_, index) => index !== 0)
    //     .map(() => ({ status: '', msg: CERT_NUM_INPUT_MSG })),
    // );
    setIsDisableInput(
      subscriptionSharedLpList
        .filter((_, index) => index !== 0) // lp 1 순위는 기본적으로 agreeYn 이 Y
        .some(data => data.agreeYn === 'Y'),
    );
  }, [subscriptionSharedLpList]);

  // 리스트 조회
  const handlerSearchList = policyNo => {
    dispatch(searchSubscriptionSharedLpList({ policyNo, planEstablishNo }));
  };

  // 인증번호 문자 발송
  const handlerSendSharedLpSms = useCallback(
    (policyNo, seq) => {
      const requestData = {
        planEstablishJointAppDTOList: subscriptionSharedLpList,
        policyNo: policyNo,
        seq: seq,
        ratioList: inputStateLpList
          .filter((_, index) => index !== 0)
          .map(obj => parseInt(obj.ratio) || 0),
        patcStepWrtNmList: inputStateLpList.map(
          obj => obj.patcStepWrtNm?.join?.() || '',
        ),
        callback: sendSharedLpSmsSuccessCallback,
      };
      dispatch(sendSharedLpSms(requestData));
      changeSendSmsButtonStatusList(seq, {
        name: 'isSendSms',
        value: true,
      });
    },
    [inputStateLpList],
  );

  // 인증번호 문자 발송 요청 성공 callback
  const sendSharedLpSmsSuccessCallback = (value, seq) => {
    if (value) {
      setCertNumList(prevState =>
        prevState.map((arr, idx) => {
          return idx + 2 === seq ? arr.map(() => '') : [...arr];
        }),
      );
      changeCertNumErrorList(seq, '', CERT_NUM_INPUT_MSG);
      setIsDisableInput(true);
    }
  };

  // 인증번호 입력 완료 후 확인 api
  const handlerCheckSharedLpSms = useCallback((policyNo, seq, smsCode) => {
    const requestData = {
      policyNo,
      seq,
      smsCode,
      callback: checkSharedLpSmsCallback,
    };

    dispatch(checkSharedLpSms(requestData));
  }, []);

  // 인증번호 확인 callback
  const checkSharedLpSmsCallback = (isSuccess, { seq, policyNo }) => {
    if (isSuccess) {
      // 재조회
      handlerSearchList(policyNo);
    } else {
      changeCertNumErrorList(seq, 'error', CERT_NUM_INPUT_ERROR_MSG);
    }
  };

  // 초기화 버튼 클릭
  const onClickResetConfirm = () => {
    dispatch(
      showModal({
        visible: true,
        type: 'confirm',
        msg: '작업 하시던 공동청약 내용이 초기화됩니다.',
        onOk: {
          handle: () => handlerReset(policyNo, lastLpOrder),
        },
      }),
    );
  };

  // 초기화
  const handlerReset = (policyNo, lastLpOrder) => {
    // handlerResetCallback(true, lastLpOrder);

    dispatch(
      resetSharedLp({
        policyNo: policyNo,
        lastLpOrder: lastLpOrder,
        callback: handlerResetCallback,
      }),
    );
  };

  // 초기화 callback
  const handlerResetCallback = (isSuccess, payload = {}) => {
    const { policyNo, lastLpOrder } = payload;

    if (isSuccess) {
      if (lastLpOrder === 2) {
        // 2순위
        setCertNumList([Array(4).fill('')]);
      } else {
        // 3 순위
        setCertNumList([Array(4).fill(''), Array(4).fill('')]);
      }

      // 재조회
      handlerSearchList(policyNo);
      setIsDisableInput(false);
    } else {
      console.log('초기화 callback fail');
    }
  };

  const getIsAllAgreed = () =>
    inputStateLpList
      // .filter((_, index) => index !== 0) // 자기 자신도 agreeYn: "Y"로 되어있어 제거
      .every(obj => obj.agreeYn === 'Y');

  // 완료 처리
  const handlerComplete = (policyNo, applicationNo) => {
    const isAllAgreed = getIsAllAgreed();
    if (!isAllAgreed) return;

    // if (
    //   inputStateLpList
    //     .filter((_, index) => index !== 0)
    //     .some(obj => obj.agreeYn === 'N')
    // ) {
    //   // 하나라도 인증이 안되어 있으면 단순 팝업 닫기
    //   setBizModal({
    //     visible: false,
    //   });
    //   return;
    // }

    dispatch(
      completeSharedLp({
        policyNo,
        applicationNo,
        callback: handlerCompleteCallback(policyNo),
      }),
    );
  };

  // 완료 처리 콜백
  const handlerCompleteCallback = policyNo => data => {
    if (data) {
      const message = `공동청약 확인이 완료되었습니다.`;
      // const message = `공동청약 확인이 완료되었습니다.\n보고서 등록하여 주시기 바랍니다.`;

      alert({
        message,
        onClose: () => completeClose(policyNo),
      });
    }
  };

  // 등록 후 닫기
  const completeClose = policyNo => {
    if (typeof onClose === 'function') onClose(true, policyNo);

    setBizModal({ visible: false });
  };

  // 비율 입력 이벤트
  const handlerChangeInputStateLp = data => {
    setInputStateLpList(prevState =>
      prevState.map((obj, index) => {
        return index + 1 === data.order
          ? { ...obj, [data.name]: data.value }
          : { ...obj };
      }),
    );

    if (data.name !== 'ratio') return;

    const totalRatioNumber =
      inputStateLpList
        .map((obj, index) =>
          index === data.order - 1 ? +data.value : parseInt(obj.ratio) || 0,
        )
        .reduce((acc, v) => acc + v) || 0;

    let isError = false;
    inputStateLpList.forEach((obj, index) => {
      if (isError) {
        return;
      }
      const ratioNumber =
        index + 1 === data.order ? +data.value : parseInt(obj.ratio);

      // 비율 입력 유효성 검증 안되었을때 문자 발송 버튼 불가
      if (totalRatioNumber !== 100) {
        changeAllSendSmsButtonStatusList({
          name: 'disabled',
          value: true,
        });
        isError = true;
      } else if (ratioNumber >= 100) {
        changeAllSendSmsButtonStatusList({
          name: 'disabled',
          value: true,
        });
        isError = true;
      } else if (lastLpOrder === 2 && ratioNumber % 5 !== 0) {
        changeAllSendSmsButtonStatusList({
          name: 'disabled',
          value: true,
        });
        isError = true;
      } else if (
        lastLpOrder === 3 &&
        ratioNumber % 5 !== 0 &&
        ![33, 34].includes(ratioNumber)
      ) {
        changeAllSendSmsButtonStatusList({
          name: 'disabled',
          value: true,
        });
        isError = true;
      } else if (!ratioNumber) {
        changeAllSendSmsButtonStatusList({
          name: 'disabled',
          value: true,
        });
        isError = true;
      }
    });

    // 비율 입력이 다 정상 입력 되었을때 문자 발송 상태 초기화
    if (isError) return;

    setSendSmsButtonStatusList(
      Array(lastLpOrder).fill({
        disabled: false,
        isSendSms: false,
      }),
    );
  };

  // 문자 발송 버튼 상태 수정
  const changeSendSmsButtonStatusList = (order, data) => {
    setSendSmsButtonStatusList(prevState =>
      prevState.map((obj, idx) => {
        return idx + 1 === order
          ? { ...obj, [data.name]: data.value }
          : { ...obj };
      }),
    );
  };

  // 문자 발송 버튼 상태 전체 수정
  const changeAllSendSmsButtonStatusList = data => {
    setSendSmsButtonStatusList(prevState =>
      prevState.map(obj => ({ ...obj, [data.name]: data.value })),
    );
  };

  // 인증 번호 수정
  const handlerChangeCertNum = (order, index, inputNum) => {
    const orderNumListIndex = order - 2;
    const newCertiNumList = certNumList.map((numList, numListIndex) => {
      return numListIndex === orderNumListIndex
        ? numList.map((value, numIndex) => {
            return numIndex === index ? inputNum : value;
          })
        : [...numList];
    });

    if (inputNum === '' || String(inputNum).length === 1) {
      setCertNumList(newCertiNumList);

      // focus 이동
      if (inputNum !== '' && index < 3) {
        lpRefList[order - 1].current.getCertNumInputRefList(index + 1).focus();
      }
    }

    // 현재 입력된 index의 certiNum 번호가 다 채워졌다면 인증번호 확인
    const targetCertiNum = newCertiNumList[orderNumListIndex];
    const isCertiNumFilled = targetCertiNum.every(item => item !== '');
    if (isCertiNumFilled)
      handlerCheckSharedLpSms(policyNo, order, targetCertiNum.join(''));
  };

  const handleKeyDown = (order, index, e) => {
    if (e.keyCode === 8) {
      // 백키
      if (index > 0) {
        setCertNumList(prevState =>
          prevState.map((arr, idx) => {
            return idx + 2 === order
              ? arr.map((value, idx) => {
                  return idx === index ? '' : value;
                })
              : [...arr];
          }),
        );

        lpRefList[order - 1].current.getCertNumInputRefList(index - 1).focus();
      }
    } else if (e.keyCode === 13) {
      // 엔터
      if (index < 3) {
        lpRefList[order - 1].current.getCertNumInputRefList(index + 1).focus();
      }
    }
  };

  // 인증 번호 상태 수정
  const changeCertNumErrorList = (order, status, msg) => {
    setCertNumErrorList(prevState =>
      prevState.map((obj, idx) => {
        return idx + 2 === order ? { status: status, msg: msg } : { ...obj };
      }),
    );
  };

  return (
    <>
      <header className="con-header">
        <div className="page-navi">
          <button
            type="button"
            className="btn-back"
            onClick={() => {
              if (typeof onClose === 'function') onClose(false);
              setBizModal({ visible: false });
            }}
          >
            이전
          </button>
        </div>

        <h1 className="page-tit">공동청약 확인서</h1>
      </header>

      <div className="con-wrap">
        <div className="vbox">
          {/* className : pop-box -> vbox */}
          <div className="wide-con-wrap">
            {/* 추가 */}
            {inputStateLpList &&
              inputStateLpList.map((obj, index) => (
                <JointSubscriptionRegistList
                  key={index}
                  lpOrder={index + 1}
                  ref={lpRefList?.[index]}
                  policyNo={policyNo}
                  lastLpOrder={lastLpOrder} //
                  inputStateLp={obj}
                  inputStateLpList={inputStateLpList}
                  certNum={certNumList?.[index - 1]}
                  isDisableInput={isDisableInput}
                  sendSmsButtonStatus={sendSmsButtonStatusList?.[index]}
                  handlerChangeInputStateLp={handlerChangeInputStateLp}
                  handlerSendSharedLpSms={handlerSendSharedLpSms}
                  handleKeyDown={handleKeyDown}
                  certNumError={certNumErrorList?.[index - 1]}
                  handlerChangeCertNum={handlerChangeCertNum}
                  lpDeptYn = {lpDeptYn}
                  isLP = {isLP}
                />
              ))}
            <h2 className="tit-con mgt-24">공동청약 주요내용 확인</h2>
            <div className="info-box bg-yellow">
              <ul>
                <li className="text-info4 asterisk-type mgt-0">
                  "대표 라이프 파트너 및 공동 라이프 파트너"(이하 "본인")는
                  본 보험계약을 공동으로 모집하였고 이와 관련하여 발생하는
                  제반 실적 및 보수를 회사의 관련 규정 및
                  아래의 배분율에 따라 배분하기로 합의하였음을 확인합니다.
                </li>
                <li className="text-info4 asterisk-type mgt-0">
                  본인은 공동청약과 관련하여 회사의 관련규정을 정확히
                  인지하고,향후 이로 인하여 발생되는 모든 사실상 및 법률상
                  문제에 대해서는 본인이 연대하여 책임질 것을 확인합니다.
                </li>
                <li className="text-info4 asterisk-type mgt-0">
                  본인은 공동청약과 관련하여 민원 및 분쟁이 발생할 경우,회사에
                  일체의 피해가 없도록 할 것을 확인합니다.
                </li>
              </ul>

              <ul className="num-list2 mgt-16">
                <li>
                  순위 : 역할 및 배분율 이양시 기준이 되므로 순위를 신중하게
                  결정하여 작성해 주시기 바랍니다.
                  <ul className="list-type mgl--12">
                    <li>
                      <span>1)</span>대표 라이프 파트너 해촉시, 다음 순위의 위촉
                      중인 공동 라이프 파트너가 자동적으로 대표 라이프 파트너가
                      되며 해촉 익월로부터 대표 라이프 파트너의 배분율이
                      이전됩니다.
                      <br />
                      공동 라이프 파트너 해촉시, 해촉 익월로부터 대표 라이프
                      파트너에게 해촉된 공동 라이프 파트너의 배분율이
                      이전됩니다.
                    </li>
                    <li>
                      <span>2)</span>대표 라이프 파트너가 계획 수립, 설계 및
                      대고객서비스의 주체가 됩니다.
                    </li>
                    <li>
                      <span>3)</span>모집시 역할 : 공동청약 라이프 파트너는
                      Sales Process의 한 단계에 반드시 참여해야 하며, TA 및 증권
                      전달 단계에서의 대면은 해당하지 않습니다.
                    </li>
                  </ul>
                </li>
                <li>
                  비율 : 중도에 변경할 수 없습니다.
                  <ul className="list-type mgl--12">
                    <li>
                      <span>1)</span>5%단위로만 선택 가능합니다. 단, 세 명의
                      라이프 파트너가 공동청약하는 경우에 한하여 34%, 33%, 33%로
                      신청할 수 있습니다.
                    </li>
                    <li>
                      <span>2)</span>커미션, 유지율 등 보수 및 실적은 위의
                      배분율에 따라, 배분됩니다. 또한, 대표 라이프 파트너 또는
                      공동 라이프 파트너가 해촉하는 경우에는 상기 배분율 이전
                      기준에 따라, 커미션 및 유지율이 이전되게 됩니다.
                    </li>
                  </ul>
                </li>
              </ul>
              <p className="f-14 mgt-16">
                본인은 상기의 내용을 준수할 것을 확인하며, 향후 이 사안과
                관련하여 회사에 어떠한 사실상 및 법률상의 이의제기를 하지않을
                것임을 확약합니다.
              </p>
            </div>
            {/* </div> */}
          </div>
        </div>
      </div>
      <div className="con-bottom">
        <div className="btn-area">
          {/* <div className="box-btns-left"></div> 삭제*/}
          <div className="box-btns-center">
            <div className="btns-two">
              {/* 추가 */}
              <SatButton
                label="비율초기화" //초기화 -> 비율초기화
                styleClass="btn-s b-blue btn-wide" //btn-s b-blue3 -> btn-s b-blue btn-wide
                onClick={() => onClickResetConfirm()}
              />
              <SatButton
                label="확인"
                styleClass="btn-s blue btn-wide"
                onClick={() => handlerComplete(policyNo, applicationNo)}
                disabled={
                  !getIsAllAgreed()

                  // inputStateLpList
                  //   .filter((_, index) => index != 0)
                  //   .every(obj => obj.agreeYn === 'N')
                } // 하나라도 인증 되면 활성화, 단 하나만 인증 되었을시에는 그냥 닫기 버튼 기능
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default JointSubscriptionRegistPopup;
